import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { X, Plus, AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";
import FileAttachments from "@/components/FileAttachments";

const SEDAN_IMG = "https://customer-assets.emergentagent.com/job_e418166c-e368-475e-9fd1-c54940c21065/artifacts/jpehia48_Sedan.jpg";
const SUV_IMG = "https://customer-assets.emergentagent.com/job_e418166c-e368-475e-9fd1-c54940c21065/artifacts/hb3ak8ms_SUV%20-%202.webp";
const PICKUP_IMG = "https://static.prod-images.emergentagent.com/jobs/e418166c-e368-475e-9fd1-c54940c21065/images/c3c069e8492d431e3ebc9e246703089c2e7ba4dc679423cbd61790204d7a8875.png";

const TIPOS_DANIO = [
  "Golpe", "Rayón", "Abolladura", "Rotura de cristal", "Faltante",
  "Pintura dañada", "Oxidación", "Grieta", "Otro"
];

const SEVERIDAD = [
  { value: "leve", label: "Leve", color: "bg-yellow-500" },
  { value: "moderado", label: "Moderado", color: "bg-orange-500" },
  { value: "grave", label: "Grave", color: "bg-red-600" },
];

const VehicleBodyMap = ({ marks = [], onSave, vehicleType = "Sedan", assetId }) => {
  const [localMarks, setLocalMarks] = useState(marks);
  const [addDialog, setAddDialog] = useState(false);
  const [pendingClick, setPendingClick] = useState(null);
  const [newMark, setNewMark] = useState({ tipo: "Golpe", severidad: "leve", descripcion: "", fecha: new Date().toISOString().split("T")[0] });
  const [selectedMark, setSelectedMark] = useState(null);
  const [expandedMark, setExpandedMark] = useState(null);

  const imgMap = { Sedan: SEDAN_IMG, SUV: SUV_IMG, Pickup: PICKUP_IMG };
  const imgSrc = imgMap[vehicleType] || SEDAN_IMG;

  const handleImageClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    setPendingClick({ x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10 });
    setNewMark({ tipo: "Golpe", severidad: "leve", descripcion: "", fecha: new Date().toISOString().split("T")[0] });
    setAddDialog(true);
  };

  const confirmMark = () => {
    if (!pendingClick) return;
    const mark = {
      id: Date.now().toString(),
      x: pendingClick.x,
      y: pendingClick.y,
      ...newMark,
      archivos: [],
    };
    const updated = [...localMarks, mark];
    setLocalMarks(updated);
    onSave(updated);
    setAddDialog(false);
    setPendingClick(null);
  };

  const removeMark = (markId) => {
    const updated = localMarks.filter((m) => m.id !== markId);
    setLocalMarks(updated);
    onSave(updated);
    setSelectedMark(null);
  };

  const updateMarkFiles = (markId, updatedArchivos) => {
    setLocalMarks(prev => prev.map(m =>
      m.id === markId ? { ...m, archivos: updatedArchivos } : m
    ));
  };

  const getSeverityColor = (sev) => SEVERIDAD.find((s) => s.value === sev)?.color || "bg-yellow-500";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          Haz clic sobre el diagrama para marcar una incidencia
        </p>
        <Badge variant="secondary">{localMarks.length} incidencia{localMarks.length !== 1 ? "s" : ""}</Badge>
      </div>

      {/* Vehicle Diagram */}
      <div className="relative border-2 border-dashed border-slate-300 rounded-xl bg-white overflow-hidden cursor-crosshair" data-testid="vehicle-body-map">
        <img src={imgSrc} alt="Diagrama del vehículo" className="w-full max-w-lg mx-auto select-none pointer-events-none" draggable={false} />
        <div className="absolute inset-0" onClick={handleImageClick}>
          {localMarks.map((mark) => (
            <button
              key={mark.id}
              className={`absolute w-5 h-5 rounded-full border-2 border-white shadow-lg transform -translate-x-1/2 -translate-y-1/2 transition-transform hover:scale-150 ${getSeverityColor(mark.severidad)}`}
              style={{ left: `${mark.x}%`, top: `${mark.y}%` }}
              onClick={(e) => { e.stopPropagation(); setSelectedMark(mark); }}
              title={`${mark.tipo}: ${mark.descripcion}`}
              data-testid={`mark-${mark.id}`}
            >
              <span className="sr-only">{mark.tipo}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex gap-4 text-xs text-slate-500">
        {SEVERIDAD.map((s) => (
          <div key={s.value} className="flex items-center gap-1.5">
            <div className={`w-3 h-3 rounded-full ${s.color}`} />
            {s.label}
          </div>
        ))}
      </div>

      {/* Marks List with per-mark file attachments */}
      {localMarks.length > 0 && (
        <div className="border rounded-lg divide-y max-h-[500px] overflow-auto">
          {localMarks.map((mark) => {
            const isExpanded = expandedMark === mark.id;
            return (
              <div key={mark.id}>
                <div
                  className={`flex items-center gap-3 px-3 py-2 text-sm hover:bg-slate-50 cursor-pointer ${selectedMark?.id === mark.id ? "bg-blue-50" : ""}`}
                  onClick={() => setSelectedMark(mark)}
                >
                  <div className={`w-3 h-3 rounded-full flex-shrink-0 ${getSeverityColor(mark.severidad)}`} />
                  <div className="flex-1 min-w-0">
                    <span className="font-medium">{mark.tipo}</span>
                    {mark.descripcion && <span className="text-slate-500 ml-2">— {mark.descripcion}</span>}
                  </div>
                  <span className="text-xs text-slate-400">{mark.fecha}</span>
                  {(mark.archivos?.length > 0) && (
                    <Badge variant="outline" className="text-[10px]">{mark.archivos.length} archivo{mark.archivos.length !== 1 ? "s" : ""}</Badge>
                  )}
                  {assetId && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setExpandedMark(isExpanded ? null : mark.id); }}
                      className="text-slate-400 hover:text-slate-600 p-1"
                      title="Adjuntar archivos"
                    >
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); removeMark(mark.id); }} className="text-red-400 hover:text-red-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                {/* Per-mark file attachments */}
                {isExpanded && assetId && (
                  <div className="px-4 py-3 bg-slate-50 border-t border-dashed">
                    <p className="text-xs text-slate-500 mb-2">Evidencia fotográfica (máx. 3 archivos)</p>
                    <FileAttachments
                      assetId={assetId}
                      collection="estetica-mark"
                      recordId={mark.id}
                      archivos={mark.archivos || []}
                      onUpdate={(updated) => updateMarkFiles(mark.id, updated)}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add Mark Dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Registrar Incidencia
            </DialogTitle>
            <DialogDescription>Describe el daño o desperfecto en esta zona del vehículo</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Tipo de daño</label>
                <Select value={newMark.tipo} onValueChange={(v) => setNewMark({ ...newMark, tipo: v })}>
                  <SelectTrigger data-testid="mark-tipo-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPOS_DANIO.map((t) => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Severidad</label>
                <Select value={newMark.severidad} onValueChange={(v) => setNewMark({ ...newMark, severidad: v })}>
                  <SelectTrigger data-testid="mark-severidad-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SEVERIDAD.map((s) => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Fecha</label>
              <Input type="date" value={newMark.fecha} onChange={(e) => setNewMark({ ...newMark, fecha: e.target.value })} data-testid="mark-fecha-input" />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Descripción</label>
              <Input
                value={newMark.descripcion}
                onChange={(e) => setNewMark({ ...newMark, descripcion: e.target.value })}
                placeholder="Ej: Golpe en puerta trasera derecha"
                data-testid="mark-descripcion-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddDialog(false)}>Cancelar</Button>
            <Button onClick={confirmMark} data-testid="confirm-mark-btn">
              <Plus className="w-4 h-4 mr-1" /> Agregar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Selected Mark Detail Dialog */}
      <Dialog open={!!selectedMark} onOpenChange={() => setSelectedMark(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Detalle de Incidencia</DialogTitle>
          </DialogHeader>
          {selectedMark && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Tipo:</span> <span className="font-medium">{selectedMark.tipo}</span></div>
                <div><span className="text-slate-500">Severidad:</span> <Badge className={`${getSeverityColor(selectedMark.severidad)} text-white ml-1`}>{SEVERIDAD.find(s => s.value === selectedMark.severidad)?.label}</Badge></div>
                <div><span className="text-slate-500">Fecha:</span> <span className="font-medium">{selectedMark.fecha}</span></div>
                <div><span className="text-slate-500">Posición:</span> <span className="font-mono text-xs">{selectedMark.x}%, {selectedMark.y}%</span></div>
              </div>
              {selectedMark.descripcion && (
                <div className="text-sm"><span className="text-slate-500">Descripción:</span> <p className="mt-1">{selectedMark.descripcion}</p></div>
              )}
              {/* File attachments in detail dialog */}
              {assetId && (
                <div className="pt-2 border-t">
                  <p className="text-sm font-medium text-slate-700 mb-2">Evidencia Fotográfica</p>
                  <FileAttachments
                    assetId={assetId}
                    collection="estetica-mark"
                    recordId={selectedMark.id}
                    archivos={selectedMark.archivos || []}
                    onUpdate={(updated) => {
                      updateMarkFiles(selectedMark.id, updated);
                      setSelectedMark(prev => ({ ...prev, archivos: updated }));
                    }}
                  />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="destructive" size="sm" onClick={() => removeMark(selectedMark?.id)}>
              <X className="w-4 h-4 mr-1" /> Eliminar Incidencia
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VehicleBodyMap;
